import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../supabase/client';
import { orderService, Order } from '../utils/orderService';
import { DURATION_LABELS } from '../utils/orderUtils';

export type OrderListType =
  | 'student-center' | 'admin-all-student' | 'admin-student'
  | 'teacher-center' | 'admin-all-teacher' | 'admin-teacher';

export interface OrderFilterExt {
  searchQuery?: string;
  status?: string;
  duration?: string;
  source?: string;
  dateRange?: { start: string; end: string };
}

interface UseOrdersOptions {
  type: OrderListType;
  targetUid?: string;
  filterExt?: OrderFilterExt;
  pageSize?: number;
  enabled?: boolean;
}

interface UseOrdersResult {
  orders: Order[];
  filteredOrders: Order[];
  paginatedOrders: Order[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  filteredCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  refresh: () => void;
  stats: {
    total: number;
    paid: number;
    pending: number;
    expired: number;
    refunded: number;
    cancelled: number;
  };
}

function getRoleFromType(type: OrderListType): 'student' | 'teacher' {
  if (type === 'teacher-center' || type === 'admin-all-teacher' || type === 'admin-teacher') {
    return 'teacher';
  }
  return 'student';
}

/**
 * 统一订单数据 Hook
 * 从统一的 orders 表查询，通过 role 字段区分学员/教师
 */
export function useOrders({
  type,
  targetUid,
  filterExt,
  pageSize = 10,
  enabled = true,
}: UseOrdersOptions): UseOrdersResult {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const role = getRoleFromType(type);

  const loadOrders = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);

    try {
      let rawOrders: Order[] = [];

      if (type === 'student-center' || type === 'teacher-center') {
        // 当前用户的订单
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setOrders([]); return; }
        rawOrders = await orderService.getUserOrders(user.id);
      } else if (type === 'admin-student' || type === 'admin-teacher') {
        // 指定用户的订单
        if (!targetUid) { setOrders([]); return; }
        rawOrders = await orderService.getUserOrders(targetUid);
      } else if (type === 'admin-all-student' || type === 'admin-all-teacher') {
        // 管理员查看所有订单（按角色过滤）
        const { data: ordersData } = await supabase
          .from('orders')
          .select('*')
          .eq('role', role)
          .order('created_at', { ascending: false });

        if (ordersData && ordersData.length > 0) {
          const userIds = [...new Set(ordersData.map((o: any) => o.user_id).filter(Boolean))];
          const { data: profilesData } = await supabase
            .from('profiles')
            .select('id, username, phone')
            .in('id', userIds);

          const userMap: Record<string, { username: string; phone: string }> = {};
          profilesData?.forEach(p => {
            userMap[p.id] = {
              username: p.username || '',
              phone: p.phone?.includes('@') ? p.phone.split('@')[0] : p.phone || '',
            };
          });

          rawOrders = ordersData.map((o: any) => ({
            id: o.id,
            userId: o.user_id,
            userName: userMap[o.user_id]?.username || '未知用户',
            userPhone: userMap[o.user_id]?.phone || '-',
            // 教师订单别名字段
            teacherName: role === 'teacher' ? (userMap[o.user_id]?.username || '未知用户') : undefined,
            teacherPhone: role === 'teacher' ? (userMap[o.user_id]?.phone || '-') : undefined,
            orderNo: o.order_no || o.id,
            orgId: '',
            orgName: o.organization || '-',
            catId: '',
            catName: o.category || '-',
            levelId: '',
            levelText: o.level_text || '-',
            duration: o.duration_type as Order['duration'],
            durationLabel: DURATION_LABELS[o.duration_type] || o.duration_type || '-',
            originalPrice: Number(o.original_price) || Number(o.price) || 0,
            price: o.actual_price != null ? Number(o.actual_price) : Number(o.price) || 0,
            actualPrice: o.actual_price != null ? Number(o.actual_price) : undefined,
            pointsDeducted: o.points_deducted || undefined,
            deductionAmount: o.deduction_amount ? Number(o.deduction_amount) : undefined,
            status: o.status,
            payMethod: o.pay_method || undefined,
            payTime: o.paid_at,
            expireDate: o.expire_date || '-',
            createdAt: o.created_at,
            updatedAt: o.updated_at,
            source: o.package_source || o.source || 'self',
            refundDestination: o.refund_destination || undefined,
            refundAmount: o.refund_amount ? Number(o.refund_amount) : undefined,
          }));
        }
      }

      setOrders(rawOrders);
    } catch (err) {
      setError((err as Error).message || '加载订单失败');
    } finally {
      setLoading(false);
    }
  }, [type, targetUid, enabled, refreshKey, role]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // 前端筛选
  const filteredOrders = useMemo(() => {
    if (!filterExt) return orders;

    return orders.filter(order => {
      if (filterExt.searchQuery) {
        const q = filterExt.searchQuery.toLowerCase();
        const matchSearch =
          (order.orderNo || '').toLowerCase().includes(q) ||
          ((order as any).userName || '').toLowerCase().includes(q) ||
          ((order as any).userPhone || '').includes(q);
        if (!matchSearch) return false;
      }
      if (filterExt.status && filterExt.status !== 'all' && order.status !== filterExt.status) return false;
      if (filterExt.duration && filterExt.duration !== 'all') {
        if (order.duration !== filterExt.duration) return false;
      }
      if (filterExt.source && filterExt.source !== 'all' && (order as any).source !== filterExt.source) return false;
      if (filterExt.dateRange?.start) {
        if (new Date(order.createdAt) < new Date(filterExt.dateRange.start)) return false;
      }
      if (filterExt.dateRange?.end) {
        if (new Date(order.createdAt) > new Date(filterExt.dateRange.end + 'T23:59:59')) return false;
      }
      return true;
    });
  }, [orders, filterExt]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedOrders = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, safeCurrentPage, pageSize]);

  const stats = useMemo(() => ({
    total: orders.length,
    paid: orders.filter(o => o.status === 'paid').length,
    pending: orders.filter(o => o.status === 'pending').length,
    expired: orders.filter(o => o.status === 'expired').length,
    refunded: orders.filter(o => o.status === 'refunded').length,
    cancelled: orders.filter(o => (o.status as string) === 'cancelled').length,
  }), [orders]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return {
    orders,
    filteredOrders,
    paginatedOrders,
    loading,
    error,
    totalCount: orders.length,
    filteredCount: filteredOrders.length,
    currentPage: safeCurrentPage,
    totalPages,
    pageSize,
    setCurrentPage,
    refresh,
    stats,
  };
}
